import React, { Children, useEffect, useRef, useState } from "react";
import * as Yup from "yup";
import { Box, Button, Divider, Grid, Typography } from "@mui/material";
import { StyledButton } from "../../../../../../shared/components/buttons/custom-button";
import FormBuilderV4 from "../../../../../../shared/components/form-builder/form-builder-v4";
import {
  BuyerSellerDetails,
  buyerSellerDetails,
  getTcSellerInformation,
  getTcBuyerInformation,
  INonCertifiedTrader,
  mapBuyerSellerDetailsApiResponseToInitialValues,
  mapReturnTcAutofillFromSource,
  mapPayload,
} from "../../../tc-models/tc-req-dto/tc-buyer-seller-details.req_dto";
import { buyerSellerDetailsService } from "../../../tc-service/buyer-seller-details.service";
import { NavLink, useNavigate, useParams } from "react-router-dom";
import useNotification from "../../../../../../shared/hooks/useNotification";
import { useFormActions } from "../../../../../../shared/hooks/form-builder.hooks";
import { useApi } from "../../../../../../shared/hooks/useApi";
import { LoaderComponent } from "../../../../../../shared/components/loaders/page-loader";
import {
  TC_CREATE_ROUTE_CONFIG,
  TCAppConfig,
} from "../../../../../../shared/app-configs/tc-app-configs";
import { translateTcV4ValidationDetail } from "../../../../../../shared/utils/tc-v4-validation-messages.utils";
import { dtrackItService } from "../../../tc-service/dtrackit.service";
import { CustomButton } from "../../../../../../shared/components/buttons/buttons";
import ModalPopup from "../../../../../../shared/components/modals/custom-popup";
import RawMaterialInfo from "./non-certified-trader";
import { ITcProductRawMaterialInformation } from "../../../tc-models/tc-req-dto/tc-certified-product-details.req_dto";
import NonCertifiedTraderInfo from "./non-certified-trader";
import { useFormik } from "formik";
import { Utills } from "../../../../../../shared/app-configs/utilit";
import { useTransactionCertificateStore } from "../../../../../../store/transaction-certificates.store";
import { InnerButton } from "../../../../../../shared/components/buttons/floating-button-container";
import { RestrictedDataValidation } from "../../../../../../shared/utils/custom-validation.utils";
import {
  applyApiFieldErrorsToFormik,
  extractApiFieldErrors,
  scrollToFirstFormError,
} from "../../../../../../shared/utils/form-error.utils";
import { standardCodeFromValue } from "../incoming-tc-details/incoming-details-apis";
import {
  filterCountryOptionsByState,
  getCountryCodeFromState,
  hasInvalidPartyLocationValue,
  normalizeTownFromLookup,
  unwrapScSearchItems,
  validateTcIssueDateAgainstSc,
} from "./buyer-seller-lookup.utils";
import {
  findReturnTcPartyMismatches,
  returnTcPartyMismatchMessage,
} from "./return-tc-party-validation";
import { getBuyerSellerDetailsValidationSchema } from "./buyer-seller-validation.schema";

const TE_ID_PATTERN = /^TE-\d{8}$/;
const isNaValue = (value?: string | null) =>
  typeof value === "string" && value.trim().toLowerCase() === "na";

const hasRestrictedPlaceholder = (value?: string | null, allowNa = false) => {
  if (!value) return false;
  if (allowNa && isNaValue(value)) return false;
  return RestrictedDataValidation.isRestrictedData(value);
};

// Backend buyer/seller validation errors use the nested API payload names —
// map them to the flat Formik field names so the exact inputs highlight inline.
const BUYER_SELLER_API_FIELD_TO_FORM_FIELD: Record<string, string> = {
  "tcSellerInformation.sellerTeId": "sellerTEId",
  "tcSellerInformation.sellerName": "sellerName",
  "tcSellerInformation.sellerStreetAddress": "sellerAddress1",
  "tcSellerInformation.sellerTown": "sellerTown",
  "tcSellerInformation.sellerPostcode": "sellerPostcode",
  "tcSellerInformation.sellerStateOrProvince": "sellerStateOrProvince",
  "tcSellerInformation.sellerCountryOrArea": "sellerCountryOrArea",
  "tcSellerInformation.sellerScNo": "sellerSCNumber",
  "tcSellerInformation.sellerCertifiedOrganizationName":
    "sellerCertifiedOrganizationName",
  "tcSellerInformation.sellerCbClientNo": "sellerLicenseNumber",
  "tcBuyerInformation.buyerTeId": "buyerTEId",
  "tcBuyerInformation.buyerName": "buyerName",
  "tcBuyerInformation.buyerStreetAddress": "buyerAddress1",
  "tcBuyerInformation.buyerTown": "buyerTown",
  "tcBuyerInformation.buyerPostcode": "buyerPostcode",
  "tcBuyerInformation.buyerStateOrProvince": "buyerStateOrProvince",
  "tcBuyerInformation.buyerCountryOrArea": "buyerCountryOrArea",
  "tcBuyerInformation.buyerScNo": "buyerSCNumber",
  "tcBuyerInformation.buyerScCbLicensingCode": "buyerScCbLicensingCode",
  "tcBuyerInformation.buyerCertifiedOrganizationName":
    "buyerCertifiedOrganizationName",
};

interface SellerCertifiedProductsProps {
  onNextTab: () => Promise<void>;
}

const SellerCertifiedProducts: React.FC<SellerCertifiedProductsProps> = ({
  onNextTab,
}) => {
  const searchApi = useApi();
  const { notify } = useNotification();
  const { TE_ID, formtype } = useParams<string>();
  const hasCreateTc: boolean = formtype === "create-tc";
  const hasSubmitTc: boolean = formtype === "submited-tc";
  const hasRejectedTc: boolean = formtype === "rejected-tc";

  const [countries, setCountries] = useState([]);
  const [states, setState] = useState([]);
  const [getBuyerStates, setBuyerStates] = useState([]);
  const [getSellerStates, setSellerStates] = useState([]);
  const [buyerCountryFilterCode, setBuyerCountryFilterCode] = useState("");
  const [sellerCountryFilterCode, setSellerCountryFilterCode] = useState("");
  const [buyerScCbLicensingCodes, setBuyerScCbLicensingCodes] = useState<any[]>([]);

  const [getBuyerName, setBuyerName] = useState("");
  const [getBuyingOnBehalfOfFlag, setBuyingOnBehalfOfFlag] = useState("Yes");

  const { loading, executeGet, setLoadingState, executePost } = useApi();
  const saveApi = useApi();
  const {
    isRestrictPopUpSave,
    setCountryCodes,
    setStates,
    getTCStandardGOTS,
    cbLicensingCode,
    sellerScStatus = "",
    setSellerScStatus = () => {},
    buyerScStatus = "",
    setBuyerScStatus = () => {},
  } = useTransactionCertificateStore();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [visible, setVisible] = useState({
    sellerTEId: true,
    sellerName: true,
    buyerSeacrhTEId: true,
    disableBuyerTEId: false,
    disableBuyerTEName: false,
    buyerSearchName: true,
    buyerTEIdLabel: true,
    sellerTEIdLabel: true,
  });
  const [disable, setDisable] = useState({
    sellerCountry: true,
    sellerState: true,
    buyerCountry: true,
    buyerState: true,
    sellerPostCode: true,
    buyerPostCode: true,
    sellerTown: true,
    buyerTown: true,
    buyerAddress1: true,
    sellerAddress1: true,
    // Unlike the fields above (which are always auto-derived and so start
    // locked), a brand-new Return TC has no SC No yet -- these start
    // unlocked and only lock once handleSellerSCValueChange/
    // handleBuyerSCValueChange or the data-load effect below finds a value.
    sellerLicenseNumber: false,
    sellerCertifiedOrganizationName: false,
    buyerLicenseNumber: false,
    buyerCertifiedOrganizationName: false,
  });

  const [sellerScStandards, setSellerScStandards] = useState<string[]>([]);
  const [tcStandards, setTcStandards] = useState<string[]>([]);
  const [tcStatus, setTcStatus] = useState<string>("");
  const [isReturnTc, setIsReturnTc] = useState(false);
  // Expected return TC parties (roles swapped) captured from the incoming/source
  // TC so Save can pre-check the fields the validation sheet requires to match.
  const returnSourcePartiesRef = useRef<BuyerSellerDetails | null>(null);
  const [isBuyerTeCertifiedVisbile, setBuyerTeCertifiedVisbile] = useState<boolean>(true);
  const [tcIssueDate, setTcIssueDate] = useState<string>("");
  const [initialValues, setInitialValues] = useState<BuyerSellerDetails>(buyerSellerDetails);
  const [isDataInitialized, setIsDataInitialized] = useState<boolean>(false);
  const nonCertifiedTraderDatas: INonCertifiedTrader[] = [];

  const [stepData, setStepData] = useState({
    nonCertifiedTrader: nonCertifiedTraderDatas,
  });
  const navigate = useNavigate();

  const { getCurrentFormValues, updateField } = useFormActions(
    "buyer-seller-products"
  );
  const { getCurrentFormValues: getTransactionDetailsFormValues } = useFormActions(
    "Transaction-Details"
  );

  const setZustandInitialState = (initial?: any) => {
    const defalutState: any = initialValues;
    Object.entries(initial ?? defalutState).forEach(([key, value]) => {
      updateField(key, value);
    });
  };



const TE_ID_PATTERN = /^TE-\d{8}$/;
const isNaValue = (value?: string | null) =>
  typeof value === "string" && value.trim().toLowerCase() === "na";

const isReturnValue = (value?: string | null) => {
  const normalized = String(value || "").trim().toUpperCase();
  return normalized === "Y" || normalized === "YES";
};

const hasRestrictedPlaceholder = (value?: string | null, allowNa = false) => {
  if (!value) return false;
  if (allowNa && isNaValue(value)) return false;
  return RestrictedDataValidation.isRestrictedData(value);
};

// DAT-501 "seller SC Number" rule 3 / "tc standard" submission rule: every standard
// selected for the TC must be present on the attached Seller SC (86ajey1h1).
// Returns the TC standards the SC does NOT carry; both sides are normalized to
// short codes so v3 SCs storing full labels still compare correctly. Skipped
// (returns []) when either list is unknown — the submit-time backend check stays
// authoritative for those.
const missingSellerScStandards = (
  tcStandards: string[],
  scStandards: string[]
): string[] => {
  if (!Array.isArray(tcStandards) || !Array.isArray(scStandards)) return [];
  if (tcStandards.length === 0 || scStandards.length === 0) return [];
  const scCodes = new Set(
    scStandards.map(standardCodeFromValue).filter(Boolean)
  );
  return tcStandards.filter((standard) => {
    const code = standardCodeFromValue(standard);
    return code && !scCodes.has(code);
  });
};





  const buyerSellerDetailsValidationSchema = getBuyerSellerDetailsValidationSchema(tcStatus, sellerScStatus, isReturnTc);

  // On an Amendment/Invalid/Rejected(-Amendment/-Invalid) TC, an existing
  // seller/buyer SC only needs to exist -- its status may be Valid, Expired,
  // Suspended or Withdrawn -- same relaxed set already allowed for a Return
  // TC. Kept in sync with the tcStatus lists used by checkScExists below.
  const isRelaxedScStatusTc = isReturnTc || ["A", "I", "R", "RA", "RI"].includes(tcStatus);

  const fetchSCDetailsFromAPI = async (query: string, teId: string, dropDown: 'Buyer' | 'Seller') => {
    const trimmedQuery = String(query || "").trim();
    try {
      if (!teId || teId === "") {
        return [];
      }
      const selectedCbLicensingCode =
        dropDown === "Buyer"
          ? formRef.current?.values?.buyerScCbLicensingCode
          : cbLicensingCode;
      if (!selectedCbLicensingCode) {
        if (trimmedQuery) {
          notify(`Please select ${dropDown} CB Licensing Code first`, "warning");
        }
        return [];
      }
      const response: any = await searchApi.executeGet(
        TCAppConfig.GET_BUYER_SELLER_SC_NO_V4,
        {
          scNo: query,
          scCbLicensingCode: selectedCbLicensingCode,
          teId: teId,
          tcId: TE_ID,
          dropDown: dropDown,
          // A seller/buyer SC may be in any status (Expired/Suspended/Withdrawn
          // included), not just Valid -- otherwise a non-Valid SC that's
          // perfectly legal to keep on the TC never shows up to be selected in
          // the live search dropdown in the first place.
          isReturnTc: isRelaxedScStatusTc ? "true" : "false"
        }
      );
      
      const items = unwrapScSearchItems(response?.data);
      if (trimmedQuery && items.length === 0) {
        if (dropDown === "Seller") {
          formRef.current?.setFieldValue("sellerSCNumber", "");
          formRef.current?.setFieldValue("sellerCertifiedOrganizationName", "");
          formRef.current?.setFieldValue("sellerLicenseNumber", "");
          setSellerScStandards([]);
        } else {
          formRef.current?.setFieldValue("buyerSCNumber", "");
          formRef.current?.setFieldValue("buyerCertifiedOrganizationName", "");
          formRef.current?.setFieldValue("buyerLicenseNumber", "");
        }
        notify(
          dropDown === "Seller"
            ? `No valid Seller SC found for "${trimmedQuery}" for the given seller TE-ID: ${teId}`
            : `No valid Buyer SC found for "${trimmedQuery}"`,
          "error"
        );
      }
      return items.map((item: any) => ({
        label: item.scNo,
        value: item.scNo,
        scNo: item.scNo,                    // Add for sellerSCValueChange
        certifiedOrganizationName: item.certifiedOrganizationName,
        certifiedOrganizationCbClientNo: item.certifiedOrganizationCbClientNo || "",
        scCbLicensingCode: item.scCbLicensingCode || selectedCbLicensingCode,
        scStandard: item.scStandard || [],
        scValidFromDate: item.scValidFrom || item.scValidFromDate || item.validFrom || item.validFromDate || "",
        scValidUntilDate: item.scValidUntil || item.scValidUntilDate || item.validUntil || item.validUntilDate || "",
        scExtendedUntilDate: item.scValidExtendedUntil || item.scExtendedUntilDate || item.scValidExtendedUntilDate || item.extendedUntil || item.extendedUntilDate || "",
        scIssueDate: item.scIssueDate || item.scIssue || item.issueDate || item.scCreatedDate || item.createdDate || "",
        scVersion: item.scVersion || item.version || item.scVersionNo || item.scSchemaVersion || "",
        scStatus: item.scStatus || item.status || "",
        scStatusDescription: item.scStatusDescription || item.statusDescription || ""
      }));
    } catch (error) {
      console.error("Error fetching SC details:", error);
      if (trimmedQuery) {
        notify(`Unable to search ${dropDown} SC numbers. Please try again.`, "error");
      }
      return [];
    }
  };

  const applySellerTownFromLookup = (city: unknown) => {
    const normalizedTown = normalizeTownFromLookup(city);
    setDisable((prev) => ({
      ...prev,
      sellerTown: normalizedTown.length > 0,
    }));
    formRef.current?.setFieldValue("sellerTown", normalizedTown);
  };
  
  const handleSellerSCValueChange = (value: any) => {
    if (value == null) {
      formRef.current.setFieldValue("sellerSCNumber", "");
      formRef.current.setFieldValue("sellerCertifiedOrganizationName", "");
      formRef.current.setFieldValue("sellerLicenseNumber", "");
      setSellerScStandards([]); // Clear standards
      setSellerScStatus("");
      // Once the SC No is cleared (the "X" in the search field), the dependent
      // CB Client Number / Certified Organization Name are editable again --
      // same "disabled only while a value is actually present" rule already
      // used for Seller Town/Postcode elsewhere on this tab.
      setDisable((prev) => ({
        ...prev,
        sellerLicenseNumber: false,
        sellerCertifiedOrganizationName: false,
      }));
      return;
    }

    // A manually-committed "na" (non-certified seller, Return TC only) has no
    // real SC match behind it -- just the typed label, no scNo/standards/dates
    // -- so none of the match-quality checks below apply to it.
    const isNaCommit = !value.scNo && !value.value && !!value.label;

    if (!isNaCommit) {
      const buyerSCNumber = formRef.current.values.buyerSCNumber;
      if (buyerSCNumber && (value.value === buyerSCNumber || value.scNo === buyerSCNumber)) {
        notify("Seller SC Number must be different from Buyer SC Number", "error");
        formRef.current.setFieldValue("sellerSCNumber", "");
        formRef.current.setFieldValue("sellerCertifiedOrganizationName", "");
        formRef.current.setFieldValue("sellerLicenseNumber", "");
        setSellerScStandards([]);
        setSellerScStatus("");
        return;
      }
      // DAT-501 seller-SC rule 3: every TC standard must be present on the SC —
      // reject the selection immediately so the mismatch never reaches save (86ajey1h1).
      const missingStandards = isReturnTc
        ? []
        : missingSellerScStandards(tcStandards, value.scStandard || []);
      if (missingStandards.length > 0) {
        notify(
          `Selected Seller SC does not include the TC standard(s): ${missingStandards.join(
            ", "
          )}. Select a different Seller SC or change the TC standard.`,
          "error"
        );
        formRef.current.setFieldValue("sellerSCNumber", "");
        formRef.current.setFieldValue("sellerCertifiedOrganizationName", "");
        formRef.current.setFieldValue("sellerLicenseNumber", "");
        setSellerScStandards([]);
        setSellerScStatus("");
        return;
      }

      if (!isReturnTc && tcIssueDate) {
        const scDateValidationError = validateTcIssueDateAgainstSc(tcIssueDate, value);
        if (scDateValidationError) {
          notify(scDateValidationError, "error");
          formRef.current.setFieldValue("sellerSCNumber", "");
          formRef.current.setFieldValue("sellerCertifiedOrganizationName", "");
          formRef.current.setFieldValue("sellerLicenseNumber", "");
          setSellerScStandards([]);
          setSellerScStatus("");
          return;
        }
      }
    }

    formRef.current.setFieldValue("sellerSCNumber", value.value || value.scNo || value.label);
    formRef.current.setFieldValue("sellerCertifiedOrganizationName", value.certifiedOrganizationName || "");
    // Seller CB Client Number is autofilled (read-only) from the selected SC's certified org
    formRef.current.setFieldValue("sellerLicenseNumber", value.certifiedOrganizationCbClientNo || "");
    setSellerScStandards(value.scStandard || []);
    setSellerScStatus(String(value.scStatus || value.status || "").trim().toUpperCase());
    // Once the SC No has a value (a real match or the "na" sentinel), lock the
    // dependent CB Client Number / Certified Organization Name.
    setDisable((prev) => ({
      ...prev,
      sellerLicenseNumber: true,
      sellerCertifiedOrganizationName: true,
    }));
  };

  const handleBuyerSCValueChange = (value: any) => {
    if (value == null) {
      formRef.current.setFieldValue("buyerSCNumber", "");
      formRef.current.setFieldValue("buyerCertifiedOrganizationName", "");
      setBuyerScStatus("");
      if (isReturnTc) {
        formRef.current.setFieldValue("buyerLicenseNumber", "");
        setDisable((prev) => ({
          ...prev,
          buyerLicenseNumber: false,
          buyerCertifiedOrganizationName: false,
        }));
      }
      return;
    }
    formRef.current.setFieldValue("buyerSCNumber", value.value || value.scNo || value.label);
    formRef.current.setFieldValue("buyerCertifiedOrganizationName", value.certifiedOrganizationName || "");
    formRef.current.setFieldValue("buyerScCbLicensingCode", value.scCbLicensingCode || "");
    setBuyerScStatus(String(value.scStatus || value.status || "").trim().toUpperCase());
    const cbClientNo = value.certifiedOrganizationCbClientNo || value.buyerCbClientNo || value.buyerLicenseNo || value.cbClientNo || value.buyerLicenseNumber || "";
    if (cbClientNo || isReturnTc) {
      formRef.current.setFieldValue("buyerLicenseNumber", cbClientNo);
      if (isReturnTc) {
        setDisable((prev) => ({
          ...prev,
          buyerLicenseNumber: true,
          buyerCertifiedOrganizationName: true,
        }));
      }
    }
  };

  useEffect(() => {
    // Apply a fully-mapped BuyerSellerDetails object to the form: visibility flags,
    // disabled states, dependent state dropdowns, and the initial Formik/Zustand values.
    const trimObjectStrings = (obj: any): any => {
      if (!obj || typeof obj !== "object") return obj;
      if (Array.isArray(obj)) return obj.map(trimObjectStrings);
      const copy: any = {};
      for (const key of Object.keys(obj)) {
        const val = obj[key];
        if (typeof val === "string") {
          copy[key] = val.trim();
        } else if (typeof val === "object" && val !== null) {
          copy[key] = trimObjectStrings(val);
        } else {
          copy[key] = val;
        }
      }
      return copy;
    };

    const applyBuyerSellerData = async (rawDetails: BuyerSellerDetails, isReturn: boolean = false, tcStatusForCheck: string = "") => {
      const data: BuyerSellerDetails = trimObjectStrings(rawDetails);
      if (data.nonCertifiedTrader.length > 0) {
        setStepData({ nonCertifiedTrader: data.nonCertifiedTrader });
      }

      // An already-saved Amendment/Invalid/Rejected(-Amendment/-Invalid) TC may
      // carry a seller/buyer SC that's Expired/Suspended/Withdrawn -- same
      // relaxed set as a Return TC. Without this, the status lookup below comes
      // back empty for such a TC, sellerScStatus/buyerScStatus is never set, and
      // the downstream product/raw-material and required-field checks misbehave
      // as if the status were unknown.
      const allowRelaxedScStatus = isReturn || ["A", "I", "R", "RA", "RI"].includes(tcStatusForCheck);

      // Fetch seller SC status
      if (data?.sellerSCNumber && !isNaValue(data.sellerSCNumber) && data.sellerTEId) {
        try {
          const res: any = await searchApi.executeGet(
            TCAppConfig.GET_BUYER_SELLER_SC_NO_V4,
            {
              scNo: data.sellerSCNumber,
              scCbLicensingCode: cbLicensingCode,
              teId: data.sellerTEId,
              tcId: TE_ID,
              dropDown: 'Seller',
              // Seller SC may be in any status, not just Valid.
              isReturnTc: allowRelaxedScStatus ? "true" : "false"
            }
          );
          const items = unwrapScSearchItems(res?.data);
          const found = items.find((item: any) => String(item.scNo).trim().toLowerCase() === data.sellerSCNumber.trim().toLowerCase());
          if (found) {
            const statusVal = String(found.scStatus || found.status || "").trim().toUpperCase();
            setSellerScStatus(statusVal);
          }
        } catch (error) {
          console.error("Failed to load seller SC status on init:", error);
        }
      }

      // Fetch buyer SC status
      if (data?.buyerSCNumber && !isNaValue(data.buyerSCNumber) && data.buyerTEId) {
        try {
          const res: any = await searchApi.executeGet(
            TCAppConfig.GET_BUYER_SELLER_SC_NO_V4,
            {
              scNo: data.buyerSCNumber,
              scCbLicensingCode: data.buyerScCbLicensingCode || cbLicensingCode,
              teId: data.buyerTEId,
              tcId: TE_ID,
              dropDown: 'Buyer',
              // Buyer SC may be in any status, not just Valid.
              isReturnTc: allowRelaxedScStatus ? "true" : "false"
            }
          );
          const items = unwrapScSearchItems(res?.data);
          const found = items.find((item: any) => String(item.scNo).trim().toLowerCase() === data.buyerSCNumber.trim().toLowerCase());
          if (found) {
            const statusVal = String(found.scStatus || found.status || "").trim().toUpperCase();
            setBuyerScStatus(statusVal);
          }
        } catch (error) {
          console.error("Failed to load buyer SC status on init:", error);
        }
      }

      // Update visibility flags
      if (data?.sellerTEId && data?.sellerTEId !== "") {
        setVisible((prev) => ({ ...prev, sellerTEIdLabel: false }));
      }
      if (data?.buyerName) {
        setBuyerName(data?.buyerName);
      }
      if (data?.buyerTEId && data?.buyerTEId !== "") {
        setVisible((prev) => ({ ...prev, buyerTEIdLabel: false }));
      }

      // Load states based on countries. If DTrackIt supplied a state but no
      // country, derive the country from the state prefix so the state remains
      // visible and the missing-country dropdown can be restricted correctly.
      const buyerStateCountryCode = !data?.buyerCountryOrArea
        ? getCountryCodeFromState(data?.buyerStateOrProvince)
        : "";
      const sellerStateCountryCode = !data?.sellerCountryOrArea
        ? getCountryCodeFromState(data?.sellerStateOrProvince)
        : "";
      setBuyerCountryFilterCode(buyerStateCountryCode);
      setSellerCountryFilterCode(sellerStateCountryCode);
      if (data?.buyerCountryOrArea) {
        await getState(data.buyerCountryOrArea, true);
      } else if (buyerStateCountryCode) {
        await getState(buyerStateCountryCode, true);
      }
      if (data?.sellerCountryOrArea) {
        await getState(data.sellerCountryOrArea, false);
      } else if (sellerStateCountryCode) {
        await getState(sellerStateCountryCode, false);
      }

      // Handle postcode / town / address disable states (disabled when pre-filled)
      setDisable((prev) => ({
        ...prev,
        buyerPostCode: !!data?.buyerPostcode,
        sellerPostCode: !!data?.sellerPostcode,
        sellerTown: normalizeTownFromLookup(data?.sellerTown).length > 0,
        buyerTown: !!data?.buyerTown,
        sellerAddress1: !!data?.sellerAddress1,
        buyerAddress1: !!data?.buyerAddress1,
        // Same "disabled when pre-filled" rule for the SC No lookup's dependent
        // fields, so a loaded Return TC (including after switching tabs and
        // back, which remounts this component) starts locked exactly like a
        // freshly-resolved one -- not just when the resolve happens live. Only
        // applies on a Return TC (using the isReturn param, not the isReturnTc
        // state, to avoid a stale read racing this same load) -- outside
        // Return TC these fields have no dropdown-driven autofill at all.
        ...(isReturn
          ? {
              sellerLicenseNumber: !!String(data?.sellerSCNumber || "").trim(),
              sellerCertifiedOrganizationName: !!String(data?.sellerSCNumber || "").trim(),
              buyerLicenseNumber: !!String(data?.buyerSCNumber || "").trim(),
              buyerCertifiedOrganizationName: !!String(data?.buyerSCNumber || "").trim(),
            }
          : {}),
        // Handle country and state disable states (only locked when present)
        ...(data?.buyerCountryOrArea ? { buyerCountry: true } : {}),
        ...(data?.buyerStateOrProvince ? { buyerState: true } : {}),
        ...(data?.sellerCountryOrArea ? { sellerCountry: true } : {}),
        ...(data?.sellerStateOrProvince ? { sellerState: true } : {}),
      }));

      setInitialValues(data);
      setZustandInitialState(data);
      if (formRef.current) {
        formRef.current.setFieldValue("buyerLicenseNumber", data.buyerLicenseNumber || "");
        formRef.current.setFieldValue("sellerLicenseNumber", data.sellerLicenseNumber || "");
      }
    };

    // Read the return flag AND the original (source) TC id off base details.
    const fetchReturnTcFlag = async (): Promise<{ isReturn: boolean; sourceTcId: string; sourceVersion?: string }> => {
      const transactionDetails = getTransactionDetailsFormValues() || {};
      const localIsReturn = isReturnValue(transactionDetails.tcIsReturn);
      const localSourceTcId = transactionDetails.returnTcId || "";
      const localSourceVersion = transactionDetails.returnTcSourceVersion || "";

      if (localIsReturn && localSourceTcId) {
        setIsReturnTc(localIsReturn);
        setTcStandards(
          Array.isArray(transactionDetails.tcStandard) ? transactionDetails.tcStandard : []
        );
        setTcIssueDate(transactionDetails.tcIssueDate || "");
        return { isReturn: localIsReturn, sourceTcId: localSourceTcId, sourceVersion: localSourceVersion };
      }

      if (!TE_ID) return { isReturn: false, sourceTcId: "", sourceVersion: "" };
      try {
        const response: any = await searchApi.executeGet(
          TCAppConfig.GET_TC_BASE_DETAILS_V4,
          { tcId: TE_ID }
        );
        const isReturn = isReturnValue(response?.data?.tcIsReturn);
        setIsReturnTc(isReturn);
        setTcStandards(
          Array.isArray(response?.data?.tcStandard) ? response.data.tcStandard : []
        );
        setTcIssueDate(response?.data?.tcIssueDate || "");
        return {
          isReturn,
          sourceTcId: response?.data?.returnTcId || "",
          sourceVersion: response?.data?.returnTcSourceVersion || "",
        };
      } catch (err) {
        console.error("Failed to fetch TC return flag:", err);
        return { isReturn: false, sourceTcId: "", sourceVersion: "" };
      }
    };

    // Load this TC's own saved buyer/seller. If none exists yet (or un-swapped default draft)
    // and this is a Return TC, auto-fill (with roles swapped) from the original/source TC.
    const fetchTcBaseDetails = async (isReturn: boolean, sourceTcId: string, sourceVersion?: string, tcStatusForCheck: string = "") => {
      try {
        let ownData: BuyerSellerDetails | null = null;
        if (TE_ID) {
          const response: any = await executeGet(
            TCAppConfig.GET_TC_BUYERSSELLERS_DETAILS_V4,
            { tcId: TE_ID }
          );
          const ownSeller = getTcSellerInformation(response?.data);
          const ownBuyer = getTcBuyerInformation(response?.data);
          const hasOwnData = !!(ownSeller?.sellerTeId || ownBuyer?.buyerTeId);

          if (response?.data && hasOwnData) {
            if (ownSeller?.sellerScStandard) {
              setSellerScStandards(ownSeller.sellerScStandard);
            }
            ownData = mapBuyerSellerDetailsApiResponseToInitialValues(response.data);
          }
        }

        if (isReturn && sourceTcId) {
          // Return TC with no saved swapped parties yet → auto-fill from the original TC.
          // Roles swap: return seller <- source buyer, return buyer <- source seller.
          const isV3 = sourceVersion === "V3";
          const sourceResponse: any = await executeGet(
            isV3 ? TCAppConfig.GET_TC_BUYERSSELLERS_DETAILS : TCAppConfig.GET_TC_BUYERSSELLERS_DETAILS_V4,
            { tcId: sourceTcId }
          );
          if (sourceResponse?.data) {
            let sourceBaseData: any = {};
            try {
              const sourceBaseResponse: any = await executeGet(
                isV3 ? TCAppConfig.GET_TC_BASE_DETAILS : TCAppConfig.GET_TC_BASE_DETAILS_V4,
                { tcId: sourceTcId }
              );
              sourceBaseData = sourceBaseResponse?.data || {};
            } catch (sourceBaseError) {
              console.error("Failed to fetch source TC CB Licensing Code:", sourceBaseError);
            }
            const autoFillData = mapReturnTcAutofillFromSource(
              { ...sourceResponse.data, tcCbLicensingCode: sourceBaseData.tcCbLicensingCode },
              TE_ID || ""
            );
            returnSourcePartiesRef.current = autoFillData;

            const sourceSeller = getTcSellerInformation(sourceResponse.data);
            const sourceSellerTeId = (sourceSeller?.sellerTeId || sourceSeller?.sellerTEId || "").trim();

            // Check if own saved data has a non-empty buyer matching the source seller (i.e. already saved swapped roles).
            const isSavedWithSwappedRoles = !!(
              ownData &&
              ownData.buyerTEId &&
              ownData.sellerTEId &&
              (!sourceSellerTeId || ownData.buyerTEId === sourceSellerTeId)
            );

            if (isSavedWithSwappedRoles && ownData) {
              await applyBuyerSellerData(ownData, isReturn, tcStatusForCheck);
            } else {
              await applyBuyerSellerData(autoFillData, isReturn, tcStatusForCheck);
              notify(
                "Buyer & Seller details auto-filled from the original TC (roles swapped). Please review and Save.",
                "info"
              );
            }
          } else if (ownData) {
            await applyBuyerSellerData(ownData, isReturn, tcStatusForCheck);
          }
        } else if (ownData) {
          await applyBuyerSellerData(ownData, isReturn, tcStatusForCheck);
        }
        setIsDataInitialized(true);
      } catch (err) {
        console.error("Failed to fetch TC Base Details:", err);
        setIsDataInitialized(true);
      }
    };
    const getCountry = async () => {
      try {
        const result: any = await dtrackItService.getCountry();
        // Utills.cl(`getCountry result ${JSON.stringify(result)}`);
        const countries = result.data
          .map((t: any) => ({
            value: t.code_1,
            label: t.description_1 + " (" + t.code_1 + ")",
          }))
          .sort((a: any, b: any) =>
            a.label.localeCompare(b.label, undefined, { sensitivity: "base" })
          );
        // Utills.cl(`countries ${countries}`);
        setCountries(countries);
        setCountryCodes(countries);
        // Utills.cl(`getCountry ${countries}`)
      } catch (err) {
        Utills.cl(`Error in Fetching Organic Farm Standard Code ${err}`);
      }
    };
    const getBuyerCbLicensingCodes = async () => {
      try {
        const result: any = await dtrackItService.getCBLicensingCodes();
        const options = (Array.isArray(result?.data) ? result.data : []).map((item: any) => ({
          value: item.code_1,
          // 86ajjej2w: show the bare CB code (CB-XXX), matching the return-TC picker
          label: item.code_1,
        }));
        setBuyerScCbLicensingCodes(options);
      } catch (err) {
        console.error("Failed to fetch Buyer CB Licensing Codes:", err);
      }
    };
    const init = async () => {
      await Promise.all([getCountry(), getBuyerCbLicensingCodes()]);
      if (TE_ID) {
        // Resolve the return flag + source TC id first, then decide whether to
        // load saved parties or auto-fill them (swapped) from the original TC.
        const { isReturn, sourceTcId, sourceVersion } = await fetchReturnTcFlag();
        
        const transactionDetails = getTransactionDetailsFormValues() || {};
        let localStatus = transactionDetails.tcStatus || "";
        if (localStatus) {
          setTcStatus(localStatus);
        } else {
          try {
            const baseRes: any = await executeGet(TCAppConfig.GET_TC_BASE_DETAILS_V4, { tcId: TE_ID });
            if (baseRes?.data) {
              localStatus = baseRes.data.tcStatus || "";
              setTcStatus(localStatus);
            }
          } catch (err) {
            console.error("Failed to load tcStatus in init:", err);
          }
        }

        // Pass the freshly-read status straight through (not the tcStatus state,
        // which won't reflect the setTcStatus above until the next render) --
        // same "avoid a stale read racing this same load" reasoning as isReturn.
        await fetchTcBaseDetails(isReturn, sourceTcId, sourceVersion, localStatus);
      } else {
        setZustandInitialState();
        setIsDataInitialized(true);
      }
    };
    init();
    // //alert(typeof getTCStandard)
  }, []);

  const getState = async (buyersCountryOrArea: any, isBuyer: boolean) => {
    try {
      // alert(`buyersCountryOrArea ${buyersCountryOrArea}, isBuyer ${isBuyer}`)
      const result: any = await dtrackItService.getStateBasedonCountry(
        buyersCountryOrArea
      );
      const list = Array.isArray(result?.data) ? result.data : [];
      const states = list.map((t: any) => ({
        value: t.code_1,
        label: `${t.description_1} (${t.code_1})`,
      }));
      if (isBuyer) {
        setBuyerStates(states);
      } else {
        setSellerStates(states);
      }
      // keep local and global state stores in sync
      setState(states);
      setStates(states);
    } catch (err) {
      Utills.cl(`Error in Fetching Organic Farm Standard Code ${err}`);
    }
  };

  // Simulate API call for fetching options
  const fetchTEDetallsFromAPI = async (
    value: string,
    offset: number,
    type: string,
    name?: string
  ): Promise<any[]> => {
    try {
      const queryParams: Record<string, any> = {
        query_param: type == "query" ? value : "",
        offset: offset,
        name: type == "name" ? value : "",
      };
      // Fetch TE Details using the getTEDetails function
      const response = await buyerSellerDetailsService.getTEDetails(
        queryParams
      );
      const data = response.data;
      Utills.cl(
        `fetchTEDetallsFromAPI details, query ${JSON.stringify(
          queryParams
        )} response data ${JSON.stringify(data)}`
      );

      return data;
    } catch (error) {
      console.error("Error fetching TE details:", error);
      throw error; // Re-throw error to handle it in the calling code
    }
  };

  // Fields that are cleared/re-derived when the party (TE-ID / name) selection
  // changes. Their previous touched flags would surface "required" errors the
  // moment the old values are cleared — before the new selection's data has
  // been populated — so the wrappers below reset them after the change handler
  // runs. Validation still fires on Save & Next / the next explicit edit.
  const SELLER_DEPENDENT_FIELDS = [
    "sellerName",
    "sellerTEId",
    "sellerAddress1",
    "sellerAddress2",
    "sellerAddress3",
    "sellerTown",
    "sellerPostcode",
    "sellerStateOrProvince",
    "sellerCountryOrArea",
    "sellerSCNumber",
    "sellerCertifiedOrganizationName",
    "sellerLicenseNumber",
    "sellerNonCertifiedTraderName",
  ];
  const BUYER_DEPENDENT_FIELDS = [
    "buyerName",
    "buyerTEId",
    "buyerAddress1",
    "buyerAddress2",
    "buyerAddress3",
    "buyerTown",
    "buyerPostcode",
    "buyerStateOrProvince",
    "buyerCountryOrArea",
    "buyerSCNumber",
    "buyerCertifiedOrganizationName",
  ];

  const clearDependentFieldTouches = (fieldNames: string[]) => {
    if (!formRef.current) return;
    const currentTouched: Record<string, any> = {
      ...(formRef.current.touched || {}),
    };
    fieldNames.forEach((fieldName) => {
      delete currentTouched[fieldName];
    });
    formRef.current.setTouched(currentTouched, false);
  };

  const withDependentTouchReset =
    (handler: (value: any) => any, dependentFields: string[]) =>
    async (value: any) => {
      await handler(value);
      clearDependentFieldTouches(dependentFields);
    };

  // Handle selection change from SearchInput
  const handleSellerTEIdValueChange = (value: any) => {
    // if (formRef.current.values.sellingOnBehalfOfFlag === "N") {
    //   formRef.current.setFieldValue(
    //     "sellerCertifiedOrganizationName",
    //     value == null ? "" : value.account_name
    //   );
    // }
    const fields = [
      "sellerAddress1",
      "sellerTown",
      "sellerPostcode",
      "sellerStateOrProvince",
      "sellerCountryOrArea",
      "sellerName",
      "sellerTEId",
      "sellerCertifiedOrganizationName",
      "sellerLicenseNumber"
    ];
    if (value == null) {
      setSellerCountryFilterCode("");
      fields.forEach((field) => {
        formRef.current.setFieldValue(field, "");
      });
      setDisable((prev) => ({
        ...prev,
        sellerState: true,
        sellerCountry: true,
        sellerPostCode: true,
        sellerTown: true,
      }));
      setVisible((prev) => ({
        ...prev,
        sellerName: true,
        sellerTEIdLabel: true,
      }));
    } else {
      // If buyer TE ID is already present and equal to the selected seller TE ID
      const currentBuyerTEId = formRef.current?.values?.buyerTEId;
      const selectedSellerTEId = value?.TE_ID ?? value?.TEID ?? value?.TEId ?? null;
      if (currentBuyerTEId && selectedSellerTEId && currentBuyerTEId === selectedSellerTEId) {
        setSellerCountryFilterCode("");
        notify("Buyer TE ID must not be equal to Seller TE ID", "error");
        // reset only the second-selected field (seller TE ID in this handler)
        fields.forEach((field) => {
          formRef.current.setFieldValue(field, "");
        });
        setDisable((prev) => ({
          ...prev,
          sellerState: true,
          sellerCountry: true,
          sellerPostCode: true,
          sellerTown: true,
        }));
        setVisible((prev) => ({
          ...prev,
          sellerName: true,
          sellerTEIdLabel: true,
        }));
        return;
      }
      formRef.current.setFieldValue("sellerName", value.account_name);
      formRef.current.setFieldValue("sellerTEId", value.TE_ID);
      // Reset SC Number and Certified Organization Name when TE ID changes
      formRef.current.setFieldValue("sellerSCNumber", "");
      formRef.current.setFieldValue("sellerCertifiedOrganizationName", "");
      formRef.current.setFieldValue("sellerLicenseNumber", "");
      setSellerScStandards([]);
      // formRef.current.setFieldValue("sellerAddress1", value.street);
      // formRef.current.setFieldValue("sellerTown", value.city);
      // formRef.current.setFieldValue("sellerPostcode", value.postal_code);
      const sellerStateCountryCode = getCountryCodeFromState(value.state);
      setSellerCountryFilterCode(
        value.country ? "" : sellerStateCountryCode
      );
      if (value.country != null && value.country !== "") {
        // Pass plain country code to getState; the service will normalize formatting for the API
        getState(value.country, false);
      } else if (sellerStateCountryCode) {
        getState(sellerStateCountryCode, false);
      }

      if (value.street == null) {
        setDisable((prev) => ({
          ...prev,
          sellerAddress1: false,
        }));
        formRef.current.setFieldValue("sellerAddress1", "");
      } else {
        setDisable((prev) => ({
          ...prev,
          sellerAddress1: true,
        }));
        formRef.current.setFieldValue("sellerAddress1", value.street);
      }

      applySellerTownFromLookup(value.city);

      if (value.postal_code == null) {
        setDisable((prev) => ({
          ...prev,
          sellerPostCode: false,
        }));
        formRef.current.setFieldValue("sellerPostcode", "");
      } else {
        setDisable((prev) => ({
          ...prev,
          sellerPostCode: true,
        }));
        formRef.current.setFieldValue("sellerPostcode", value.postal_code);
      }
      if (value.country == null) {
        setDisable((prev) => ({
          ...prev,
          sellerCountry: false,
        }));
      } else {
        setDisable((prev) => ({
          ...prev,
          sellerCountry: true,
        }));
        formRef.current.setFieldValue("sellerCountryOrArea", value.country);
      }

      if (value.state == null) {
        setDisable((prev) => ({
          ...prev,
          sellerState: false,
        }));
      } else {
        setDisable((prev) => ({
          ...prev,
          sellerState: true,
        }));
        formRef.current.setFieldValue("sellerStateOrProvince", value.state);
      }
      setVisible((prev) => ({
        ...prev,
        sellerName: false,
        sellerTEIdLabel: false,
      }));
    }
  };

  const handleSellerNamedValueChange = (value: any) => {
    // //alert(`form ${JSON.stringify(formRef.current.values)}`)
    // if (formRef.current.values.sellingOnBehalfOfFlag === "N") {
    //   formRef.current.setFieldValue(
    //     "sellerCertifiedOrganizationName",
    //     value == null ? "" : value.account_name
    //   );
    // }
    const fields = [
      "sellerAddress1",
      "sellerAddress2",
      "sellerAddress3",
      "sellerTown",
      "sellerPostcode",
      "sellerStateOrProvince",
      "sellerCountryOrArea",
      "sellerLicenseNumber",
      "sellerNonCertifiedTraderName",
      "sellerName",
      "sellerTEId",
      "sellerCertifiedOrganizationName"
    ];
    if (value == null) {
      setSellerCountryFilterCode("");
      fields.forEach((field) => {
        formRef.current.setFieldValue(field, "");
      });
      setDisable((prev) => ({
        ...prev,
        sellerState: true,
        sellerCountry: true,
        sellerPostCode: true,
        sellerTown: true,
      }));
      setVisible((prev) => ({
        ...prev,
        sellerTEId: true,
      }));
    } else {
      // Prevent selecting the same name as the buyer
      const currentBuyerName = formRef.current?.values?.buyerName;
      const selectedSellerName = value?.account_name ?? null;
      if (currentBuyerName && selectedSellerName && currentBuyerName === selectedSellerName) {
        setSellerCountryFilterCode("");
        notify("Seller Name must not be equal to Buyer Name", "error");
        // clear only the seller fields so user can re-select
        try {
          formRef.current.setFieldValue("sellerName", "");
          formRef.current.setFieldValue("sellerTEId", "");
          formRef.current.setFieldValue("sellerAddress1", "");
          formRef.current.setFieldValue("sellerTown", "");
          formRef.current.setFieldValue("sellerPostcode", "");
          formRef.current.setFieldValue("sellerStateOrProvince", "");
          formRef.current.setFieldValue("sellerCountryOrArea", "");
          formRef.current.setFieldValue("sellerCertifiedOrganizationName", "");
          formRef.current.setFieldValue("sellerLicenseNumber", "");
          try { updateField("sellerName", ""); updateField("sellerTEId", ""); } catch (e) {}
        } catch (e) {
          fields.forEach((field) => { formRef.current?.setFieldValue(field, ""); });
        }
        setDisable((prev) => ({
          ...prev,
          sellerState: true,
          sellerCountry: true,
          sellerPostCode: true,
          sellerTown: true,
        }));
        setVisible((prev) => ({
          ...prev,
          sellerTEId: true,
        }));
        return;
      }
      formRef.current.setFieldValue("sellerName", value.account_name);
      formRef.current.setFieldValue("sellerTEId", value.TE_ID);
      // Reset SC Number and Certified Organization Name when Seller Name changes
      formRef.current.setFieldValue("sellerSCNumber", "");
      formRef.current.setFieldValue("sellerCertifiedOrganizationName", "");
      formRef.current.setFieldValue("sellerLicenseNumber", "");
      setSellerScStandards([]);
      // formRef.current.setFieldValue("sellerAddress1", value.street);
      // formRef.current.setFieldValue("sellerTown", value.city);
      // formRef.current.setFieldValue("sellerPostcode", value.postal_code);
      const sellerStateCountryCode = getCountryCodeFromState(value.state);
      setSellerCountryFilterCode(
        value.country ? "" : sellerStateCountryCode
      );
      if (value.country != null && value.country !== "") {
        let countryCode = JSON.stringify(
          `[${[value.country].map((item) => `'${item}'`)}]`
        ).replace(/^"|"$/g, "");
        getState(countryCode, false);
      } else if (sellerStateCountryCode) {
        getState(sellerStateCountryCode, false);
      }

      if (value.street == null) {
        setDisable((prev) => ({
          ...prev,
          sellerAddress1: false,
        }));
        formRef.current.setFieldValue("sellerAddress1", "");
      } else {
        setDisable((prev) => ({
          ...prev,
          sellerAddress1: true,
        }));
        formRef.current.setFieldValue("sellerAddress1", value.street);
      }

      applySellerTownFromLookup(value.city);

      if (value.postal_code == null) {
        setDisable((prev) => ({
          ...prev,
          sellerPostCode: false,
        }));
        formRef.current.setFieldValue("sellerPostcode", "");
      } else {
        setDisable((prev) => ({
          ...prev,
          sellerPostCode: true,
        }));
        formRef.current.setFieldValue("sellerPostcode", value.postal_code);
      }
      if (value.country == null) {
        setDisable((prev) => ({
          ...prev,
          sellerCountry: false,
        }));
      } else {
        setDisable((prev) => ({
          ...prev,
          sellerCountry: true,
        }));
        formRef.current.setFieldValue("sellerCountryOrArea", value.country);
      }

      if (value.state == null) {
        setDisable((prev) => ({
          ...prev,
          sellerState: false,
        }));
      } else {
        setDisable((prev) => ({
          ...prev,
          sellerState: true,
        }));
        formRef.current.setFieldValue("sellerStateOrProvince", value.state);
      }
      setVisible((prev) => ({
        ...prev,
        sellerTEId: false,
      }));
    }
  };

  const handleBuyerTEIdValueChange = async (value: any) => {
    const fields = [
      "buyerAddress1",
      "buyerTown",
      "buyerPostcode",
      "buyerStateOrProvince",
      "buyerCountryOrArea",
      "buyerName",
      "buyerTEId",
    ];
    if (value == null) {
      setBuyerCountryFilterCode("");
      setBuyerName("");
      fields.forEach((field) => {
        formRef.current.setFieldValue(field, "");
      });
      setVisible((prev) => ({
        ...prev,
        buyerSearchName: true,
      }));
      setVisible((prev) => ({
        ...prev,
        disableBuyerTEName: false,
        buyerTEIdLabel: true,
      }));
      setDisable((prev) => ({
        ...prev,
        buyerCountry: true,
        buyerState: true,
        buyerPostCode: true,
        buyerTown: true,
      }));
    } else {
      // If seller TE ID is already present and equal to the selected buyer TE ID
      const currentSellerTEId = formRef.current?.values?.sellerTEId;
      const selectedBuyerTEId = value?.TE_ID ?? value?.TEID ?? value?.TEId ?? null;
      if (currentSellerTEId && selectedBuyerTEId && currentSellerTEId === selectedBuyerTEId) {
        setBuyerCountryFilterCode("");
        notify("Seller TE ID must not be equal to Buyer TE ID", "error");
        // reset only the second-selected field (buyer TE ID in this handler)
        fields.forEach((field) => {
          formRef.current.setFieldValue(field, "");
        });
        setVisible((prev) => ({
          ...prev,
          buyerSearchName: true,
        }));
        setVisible((prev) => ({
          ...prev,
          disableBuyerTEName: false,
          buyerTEIdLabel: true,
        }));
        setDisable((prev) => ({
          ...prev,
          buyerCountry: true,
          buyerState: true,
          buyerPostCode: true,
          buyerTown: true,
        }));
        return;
      }
      formRef.current.setFieldValue("buyerName", value.account_name);
      formRef.current.setFieldValue("buyerTEId", value.TE_ID);
      // Reset SC Number and Certified Organization Name when Buyer TE ID changes.
      // For a non-certified buyer (isBuyerTeCertified === "No") keep them "na" (DAT-501:
      // Buyer SC No / Buyer Certified Organization Name autofill to "na" when not certified).
      {
        const naDefault = formRef.current.values?.isBuyerTeCertified === "No" ? "na" : "";
        formRef.current.setFieldValue("buyerSCNumber", naDefault);
        formRef.current.setFieldValue("buyerCertifiedOrganizationName", naDefault);
      }
      // formRef.current.setFieldValue("buyerAddress1", value.street);
      // formRef.current.setFieldValue("buyerPostcode", value.postal_code || "");
      setBuyerName(value.account_name);
      
      setVisible((prev) => ({
        ...prev,
        buyerSearchName: false,
        disableBuyerTEName: true,
        buyerTEIdLabel: false,
      }));
      if (getBuyingOnBehalfOfFlag == "N") {
        formRef.current.setFieldValue(
          "buyerCertifiedOrganizationName",
          value.account_name
        );
      }
      const buyerStateCountryCode = getCountryCodeFromState(value.state);
      setBuyerCountryFilterCode(
        value.country ? "" : buyerStateCountryCode
      );
      if (value.country != null && value.country !== "") {
        let countryCode = JSON.stringify(
          `[${[value.country].map((item) => `'${item}'`)}]`
        ).replace(/^"|"$/g, "");
        await getState(countryCode, true);
      } else if (buyerStateCountryCode) {
        await getState(buyerStateCountryCode, true);
      }
      if (value.street == null) {
        setDisable((prev) => ({
          ...prev,
          buyerAddress1: false,
        }));
        formRef.current.setFieldValue("buyerAddress1", "");
      } else {
        setDisable((prev) => ({
          ...prev,
          buyerAddress1: true,
        }));
        formRef.current.setFieldValue("buyerAddress1", value.street || "");
      }

      if (value.city == null) {
        setDisable((prev) => ({
          ...prev,
          buyerTown: false,
        }));
        formRef.current.setFieldValue("buyerTown", "");
      } else {
        setDisable((prev) => ({
          ...prev,
          buyerTown: true,
        }));
        formRef.current.setFieldValue("buyerTown", value.city || "");
      }
      if (value.city == null) {
        setDisable((prev) => ({
          ...prev,
          buyerTown: false,
        }));
        formRef.current.setFieldValue("buyerTown", "");
      } else {
        setDisable((prev) => ({
          ...prev,
          buyerTown: true,
        }));
        formRef.current.setFieldValue("buyerTown", value.city);
      }

      if (value.postal_code == null) {
        // alert("buyer postal code null")
        setDisable((prev) => ({
          ...prev,
          buyerPostCode: false,
        }));
        formRef.current.setFieldValue("buyerPostcode", "");
      } else {
        setDisable((prev) => ({
          ...prev,
          buyerPostCode: true,
        }));
        formRef.current.setFieldValue("buyerPostcode", value.postal_code);
      }

      if (value.country == null) {
        setDisable((prev) => ({
          ...prev,
          buyerCountry: false,
        }));
      } else {
        setDisable((prev) => ({
          ...prev,
          buyerCountry: true,
        }));
        formRef.current.setFieldValue("buyerCountryOrArea", value.country);
      }
      if (value.state == null) {
        setDisable((prev) => ({
          ...prev,
          buyerState: false,
        }));
        // //alert(disable.buyerState)
      } else {
        setDisable((prev) => ({
          ...prev,
          buyerState: true,
        }));
        formRef.current.setFieldValue("buyerStateOrProvince", value.state);
      }
    }
  };

  const handleBuyerNameValueChange = (value: any) => {
    const fields = [
      "buyerAddress1",
      "buyerTown",
      "buyerPostcode",
      "buyerStateOrProvince",
      "buyerCountryOrArea",
      "buyerName",
      "buyerTEId",
    ];
    if (value == null) {
      setBuyerCountryFilterCode("");
      setBuyerName("");
      if (getBuyingOnBehalfOfFlag == "N") {
        formRef.current.setFieldValue(
          "buyerCertifiedOrganizationName",
          ""
        );
      }
      fields.forEach((field) => {
        formRef.current.setFieldValue(field, "");
      });
      setVisible((prev) => ({
        ...prev,
        buyerSeacrhTEId: true,
        disableBuyerTEId: false,
        buyerTEIdLabel:true
      }));
      setDisable((prev) => ({
        ...prev,
        buyerCountry: true,
        buyerState: true,
        buyerPostCode: true,
        buyerTown: true,
      }));
    } else {
      // Prevent selecting the same name as the seller
      const currentSellerName = formRef.current?.values?.sellerName;
      const selectedBuyerName = value?.account_name ?? null;
      if (currentSellerName && selectedBuyerName && currentSellerName === selectedBuyerName) {
        setBuyerCountryFilterCode("");
        notify("Buyer Name must not be equal to Seller Name", "error");
        // clear only buyer fields so user can re-select
        try {
          formRef.current.setFieldValue("buyerName", "");
          formRef.current.setFieldValue("buyerTEId", "");
          formRef.current.setFieldValue("buyerAddress1", "");
          formRef.current.setFieldValue("buyerTown", "");
          formRef.current.setFieldValue("buyerPostcode", "");
          formRef.current.setFieldValue("buyerStateOrProvince", "");
          formRef.current.setFieldValue("buyerCountryOrArea", "");
          formRef.current.setFieldValue("buyerSCNumber", "");
          formRef.current.setFieldValue("buyerCertifiedOrganizationName", "");
          try { updateField("buyerName", ""); updateField("buyerTEId", ""); } catch (e) {}
        } catch (e) {
          fields.forEach((field) => { formRef.current?.setFieldValue(field, ""); });
        }
        setVisible((prev) => ({
          ...prev,
          buyerSeacrhTEId: true,
          disableBuyerTEId: false,
          buyerTEIdLabel:true
        }));
        setDisable((prev) => ({
          ...prev,
          buyerCountry: true,
          buyerState: true,
          buyerPostCode: true,
          buyerTown: true,
        }));
        setBuyerName("");
        return;
      }
      formRef.current.setFieldValue("buyerName", value.account_name);
      formRef.current.setFieldValue("buyerTEId", value.TE_ID);
      formRef.current.setFieldValue("buyerAddress1", value.street);
      // Reset SC Number and Certified Organization Name when Buyer Name changes.
      // Non-certified buyer keeps them "na" (DAT-501).
      {
        const naDefault = formRef.current.values?.isBuyerTeCertified === "No" ? "na" : "";
        formRef.current.setFieldValue("buyerSCNumber", naDefault);
        formRef.current.setFieldValue("buyerCertifiedOrganizationName", naDefault);
      }
      // formRef.current.setFieldValue("buyerTown", value.city || "");
      // formRef.current.setFieldValue("buyerPostcode", value.postal_code || "");
      setBuyerName(value?.account_name);
      if (getBuyingOnBehalfOfFlag == "N") {
        formRef.current.setFieldValue(
          "buyerCertifiedOrganizationName",
          value.account_name
        );
      }
      const buyerStateCountryCode = getCountryCodeFromState(value.state);
      setBuyerCountryFilterCode(
        value.country ? "" : buyerStateCountryCode
      );
      if (value.country != null && value.country !== "") {
        let countryCode = JSON.stringify(
          `[${[value.country].map((item) => `'${item}'`)}]`
        ).replace(/^"|"$/g, "");
        getState(countryCode, true);
      } else if (buyerStateCountryCode) {
        getState(buyerStateCountryCode, true);
      }
  
      if (value.city == null) {
        setDisable((prev) => ({
          ...prev,
          buyerTown: false,
        }));
        formRef.current.setFieldValue("buyerTown", "");
      } else {
        setDisable((prev) => ({
          ...prev,
          buyerTown: true,
        }));
        formRef.current.setFieldValue("buyerTown", value.city);
      }
  
      if (value.postal_code == null) {
        setDisable((prev) => ({
          ...prev,
          buyerPostCode: false,
        }));
        formRef.current.setFieldValue("buyerPostcode", "");
      } else {
        setDisable((prev) => ({
          ...prev,
          buyerPostCode: true,
        }));
        formRef.current.setFieldValue("buyerPostcode", value.postal_code);
      }
  
      if (value.country == null) {
        setDisable((prev) => ({
          ...prev,
          buyerCountry: false,
        }));
      } else {
        setDisable((prev) => ({
          ...prev,
          buyerCountry: true,
        }));
        formRef.current.setFieldValue("buyerCountryOrArea", value.country);
      }
  
      if (value.state == null) {
        setDisable((prev) => ({
          ...prev,
          buyerState: false,
        }));
      } else {
        setDisable((prev) => ({
          ...prev,
          buyerState: true,
        }));
        formRef.current.setFieldValue("buyerStateOrProvince", value.state);
      }
      setVisible((prev) => ({
        ...prev,
        buyerSeacrhTEId: false,
      }));
      setVisible((prev) => ({
        ...prev,
        disableBuyerTEId: true,
        buyerTEIdLabel:false
      }));
    }
  };

  const handleDataUpdate = (stepKey: string, updatedData: any) => {
    setStepData((prev) => ({
      ...prev,
      [stepKey]: updatedData,
    }));
  };
  // const testFOrmCOnfig = {
  //   pageName: "buyer-seller-products",
  //   initialValues,
  //   validationSchema: buyerSellerDetailsValidationSchema,
  //   sections: [
  //     {
  //       title: "Seller Details",
  //       fields: [
  //         {
  //           label: "testlink",
  //           name: "sellerTEId",
  //           type: "toggle",
  //           gridSize: { xs: 12, md: 4 },
  //           required: true,
  //           props: {
  //            children: <NavLink to="/test">Test</NavLink>
  //           },
  //         }
  //       ]
  //     }
  //   ],
  //   handleSubmit: async (values: any) => {
  //     try {

  //     } catch (error) {
  //       setLoadingState(false)
  //       console.error("Error saving Buyer & Seller Details:", error);
  //     }
  //   },
  // }
  const handleDialogOpen = () => {
    setDialogOpen(true);
    const zustandData = getCurrentFormValues();
    const nonCertifiedTrader =
      zustandData?.nonCertifiedTrader?.length > 0
        ? zustandData?.nonCertifiedTrader
        : [];
    setStepData({ nonCertifiedTrader: nonCertifiedTrader });
  };
  const sellerCountryOptions = filterCountryOptionsByState(
    countries,
    sellerCountryFilterCode
  );
  const buyerCountryOptions = filterCountryOptionsByState(
    countries,
    buyerCountryFilterCode
  );
  const allowedStatuses = ["V", "VALID", "E", "EXPIRED", "S", "SUSPENDED", "W", "WITHDRAWN"];

  const checkScExists = async (scNo: string, teId: string, role: "Buyer" | "Seller", cbCode: string) => {
    if (!scNo || isNaValue(scNo)) return { exists: true, status: "" };
    try {
      const response: any = await searchApi.executeGet(
        TCAppConfig.GET_BUYER_SELLER_SC_NO_V4,
        {
          scNo,
          scCbLicensingCode: cbCode,
          teId,
          tcId: TE_ID,
          dropDown: role,
          // allowedStatuses above already accepts V/E/S/W for both the A/I and
          // R/RA/RI callers of this check (86ajxfe85, 86ak22fq3) -- request the
          // same relaxed set from the backend so it doesn't get filtered out
          // before this function ever sees it (the isReturnTc name reflects
          // where the backend flag originated; the relaxed status set applies
          // to A/I existence checks the same way).
          isReturnTc: "true"
        }
      );
      const items = unwrapScSearchItems(response?.data);
      const found = items.find((item: any) => String(item.scNo).trim().toLowerCase() === scNo.trim().toLowerCase());
      if (!found) {
        return { exists: false, status: "" };
      }
      const statusVal = String(found.scStatus || found.status || "").trim().toUpperCase();
      if (allowedStatuses.includes(statusVal)) {
        return { exists: true, status: statusVal };
      }
      return { exists: false, status: statusVal };
    } catch (error) {
      console.error(`Error verifying SC number ${scNo}:`, error);
      return { exists: false, status: "" };
    }
  };

  const formConfig = {
    pageName: "buyer-seller-products",
    initialValues,
    readOnly: hasSubmitTc,
    validationSchema: buyerSellerDetailsValidationSchema,
    sections: [
      {
        title: "Seller Information",
        fields: [
          // Row 1: Column 1 - TE-ID, Column 2 - Name, Column 3 - Street Address
          {
            label: "Seller TE-ID",
            name: "sellerTEId",
            type: "search",
            gridSize: { xs: 12, md: 4 },
            required: true,
            value: initialValues.sellerTEId,
            visible: (value: any) => visible.sellerTEId,
            props: {
              placeholder: "Search Seller TE-ID",
              variant: "outlined",
              restrictInputFormat: visible.sellerTEIdLabel,
              param: "SellerTEId",
              onChange: withDependentTouchReset(
                handleSellerTEIdValueChange,
                SELLER_DEPENDENT_FIELDS
              ),
              displayKey: "TE_ID",
              onSearch: (query: string, offset: number) => {
                const response = fetchTEDetallsFromAPI(query, offset, "query");
                return response;
              },
              fullWidth: true,
            },
          },
          {
            label: "Seller TE-ID",
            name: "sellerTEId",
            type: "text",
            gridSize: { xs: 12, md: 4 },
            required: true,
            disabled: true,
            visible: (value: any) => !visible.sellerTEId,
            props: {
              placeholder: "Enter Seller Name",
              fullWidth: true,
            },
          },
          {
            label: "Seller Name",
            name: "sellerName",
            type: "search",
            gridSize: { xs: 12, md: 4 },
            required: true,
            value: initialValues.sellerName,
            visible: (value: any) => visible.sellerName,
            props: {
              placeholder: "Search Seller Name",
              variant: "outlined",
              onChange: withDependentTouchReset(
                handleSellerNamedValueChange,
                SELLER_DEPENDENT_FIELDS
              ),
              displayKey: "account_name",
              onSearch: (query: string, offset: number) => {
                Utills.cl(
                  `fetchTEDetallsFromAPI query ${query} offset ${offset}`
                );
                const response = fetchTEDetallsFromAPI(query, offset, "name");
                return response;
              },
              fullWidth: true,
            },
          },
          {
            label: "Seller Name",
            name: "sellerName",
            type: "text",
            gridSize: { xs: 12, md: 4 },
            required: true,
            disabled: true,
            visible: (value: any) => !visible.sellerName,
            props: {
              placeholder: "Enter Seller Name",
              fullWidth: true,
            },
          },
          {
            label: "Seller Street Address",
            name: "sellerAddress1",
            type: "text",
            gridSize: { xs: 12, md: 4 },
            required: true,
            disabled: disable.sellerAddress1,
            props: {
              placeholder: "Enter Seller Address",
              fullWidth: true,
            },
          },
          // Row 2 (86ajey1be): Town | Postcode | Country/Area | State/Province on one line
          {
            label: "Seller Town",
            name: "sellerTown",
            type: "text",
            gridSize: { xs: 12, md: 3 },
            required: true,
            value: initialValues.sellerTown,
            disabled: disable.sellerTown,
            helperText: 'Please fill "na" if not available',
            props: {
              placeholder: "Enter town",
              fullWidth: true,
            },
          },
          {
            label: "Seller Postcode",
            name: "sellerPostcode",
            type: "text",
            gridSize: { xs: 12, md: 3 },
            required: true,
            value: initialValues.sellerPostcode,
            disabled: disable.sellerPostCode,
            helperText: 'Please fill "na" if not available',
            props: {
              placeholder: "Enter postcode",
              fullWidth: true,
            },
          },
          {
            label: "Seller Country/Area",
            name: "sellerCountryOrArea",
            type: "select",
            gridSize: { xs: 12, md: 3 },
            required: true,
            value: initialValues.sellerCountryOrArea,
            disabled: disable.sellerCountry,
            events: {
              onChange: (newValue: any, updatedValues: any) => {
                const code = Array.isArray(newValue) ? newValue[0] : newValue;
                getState(code, false);
              },
            },
            props: {
              enableSearch: true,
              options: sellerCountryOptions,
              placeholder: "Seller Country/Area",
              fullWidth: true,
            },
          },
          {
            label: "Seller State/Province",
            name: "sellerStateOrProvince",
            type: "select",
            gridSize: { xs: 12, md: 3 },
            required: true,
            value: initialValues.sellerStateOrProvince,
            disabled: disable.sellerState,
            props: {
              options: getSellerStates,
              enableSearch: true,
              placeholder: "Seller State/Province",
              fullWidth: true,
            },
          },
          // "TC Seller Product Declarations" is intentionally NOT rendered here:
          // the declaration text is captured on the Declarations tab (86ajcuj3g).
          // The value still round-trips through this tab's save payload from the
          // loaded form state so the stored declaration is never wiped.
          // Row 3 (86ajey1be): SC Number | CB Client Number | Certified Organization Name on one line
          {
            label: "Seller SC Number",
            name: "sellerSCNumber",
            type: "search",
            gridSize: { xs: 12, md: 4 },
            required: true,
            value: initialValues.sellerSCNumber,
            // Works exactly like Seller TE-ID: pick from the search results, with
            // the field's own "X" to clear a selection. On a Return TC the party
            // may also be "na"/non-certified, which never appears as a real SC
            // search result, so Enter commits it directly as a sentinel value.
            disabled: (values: any) => !values?.sellerTEId || values?.sellerTEId === "",
            props: {
              placeholder: "Search Seller SC Number",
              variant: "outlined",
              displayKey: "label",
              allowCustomValue: isReturnTc,
              commitInputPattern: isReturnTc ? /^na$/i : undefined,
              onChange: handleSellerSCValueChange,
              onSearch: (query: string) => {
                const sellerTEId = formRef.current?.values?.sellerTEId;
                if (!sellerTEId || sellerTEId === "") {
                  notify("Please select Seller TE ID first", "warning");
                  return Promise.resolve([]);
                }
                return fetchSCDetailsFromAPI(query, sellerTEId, 'Seller');
              },
              fullWidth: true,
            },
          },
          {
            label: "Seller CB Client Number",
            name: "sellerLicenseNumber",
            type: "text",
            gridSize: { xs: 12, md: 4 },
            required: true,
            value: initialValues.sellerLicenseNumber,
            // Read-only: autofilled from the selected Seller SC's certified org CB Client
            // Number (TC DAT-501 "seller CB client No" — user shall not fill). Same rule as
            // Seller Town once Seller TE-ID is selected: disabled once the SC No has a value.
            disabled: !isReturnTc || disable.sellerLicenseNumber,
            props: {
              placeholder: "Auto-filled from Seller SC",
              variant: "outlined",
              fullWidth: true,
            },
          },
          {
            label: "Seller Certified Organization Name",
            name: "sellerCertifiedOrganizationName",
            type: "text",
            gridSize: { xs: 12, md: 4 },
            required: true,
            value: initialValues.sellerCertifiedOrganizationName,
            // Normal TCs derive this value from the selected Seller SC. Same rule as Seller
            // Town once Seller TE-ID is selected: disabled once the SC No has a value.
            disabled: !isReturnTc || disable.sellerCertifiedOrganizationName,
            props: {
              placeholder: "Auto-filled from Seller SC",
              variant: "outlined",
              fullWidth: true,
            },
          },
          {
            label: "",
            name: "nonCertifiedTrader",
            type: "component",
            gridSize: { xs: 12, md: 4, lg: 4 },
            required: true,
            children: (
              <>
                {" "}
                <CustomButton
                  sx={{
                    marginTop: "16px",
                    borderRadius: "30px",
                    padding: "10px 12px",
                    textTransform: "none",
                    backgroundColor: "#F0CD7F",
                    color: "#1C1C1C",
                    fontWeight: 400,
                    width: "100%",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    "&:hover": {
                      backgroundColor: "#E0B86F",
                      color: "#000000",
                      boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.15)",
                    },
                  }}
                  onClick={handleDialogOpen}
                  variant="contained"
                  fullWidth
                  size="medium"
                  errorMessage=""
                  label={
                    stepData?.nonCertifiedTrader.length === 0 ? (
                      <>{hasSubmitTc ? "View" : "Add"} Non-Certified Trader Information</>
                    ) : (
                      <>{hasSubmitTc ? "View" : "Edit"} Non-Certified Trader Information</>
                    )
                  }
                />
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: "2px",
                    mt: 0.5,
                  }}
                >
                  <Typography variant="caption" sx={{ fontSize: "0.8rem", color: "#333" }}>
                    {stepData?.nonCertifiedTrader.length} Records Added
                  </Typography>
                  <Typography variant="caption" sx={{ fontSize: "0.75rem", fontStyle: "italic", color: "#666" }}>
                    (Even if there is none, report na)
                  </Typography>
                </Box>
              </>
            ),
            props: {},
          },
        ],
      },
      {
        title: "Buyer Information",
        fields: [
          // {
          //   label: "Does the Buyer have a TE-ID?",
          //   name: "isBuyerTeCertified",
          //   type: "radio",
          //   gridSize: { xs: 12, md: 4 },
          //   required: false,
          //   value: initialValues.isBuyerTeCertified,
          //   events: {
          //     onChange: (value: any) => {
          //       Utills.cl("buyer te certified", value);
          //       if (value === "Yes") {
          //         formRef.current.setFieldValue("buyerStateOrProvince", "");
          //         formRef.current.setFieldValue("buyerCountryOrArea", "");
          //         formRef.current.setFieldValue("buyerTEId", "");
          //         formRef.current.setFieldValue("buyerName", "");
          //         formRef.current.setFieldValue("buyerAddress1", "");
          //         formRef.current.setFieldValue("buyerAddress2", "");
          //         formRef.current.setFieldValue("buyerAddress3", "");
          //         formRef.current.setFieldValue("buyerTown", "");
          //         formRef.current.setFieldValue("buyerPostcode", "");
          //         formRef.current.setFieldValue("buyerLicenseNumber", "");

          //         setBuyerTeCertifiedVisbile(true);
          //         setVisible((prev) => ({
          //           ...prev,
          //           buyerSearchName: true,
          //           buyerSeacrhTEId: true,
          //           disableBuyerTEId: true,
          //           disableBuyerTEName: true,
          //         }));
          //         setDisable((prev) => ({
          //           ...prev,
          //           buyerCountry: true,
          //           buyerState: true,
          //           buyerPostCode: true,
          //         }));
          //       } else {
          //         formRef.current.setFieldValue("buyerStateOrProvince", "");
          //         formRef.current.setFieldValue("buyerCountryOrArea", "");
          //         formRef.current.setFieldValue("buyerTEId", "");
          //         formRef.current.setFieldValue("buyerName", "");
          //         formRef.current.setFieldValue("buyerAddress1", "");
          //         formRef.current.setFieldValue("buyerAddress2", "");
          //         formRef.current.setFieldValue("buyerAddress3", "");
          //         formRef.current.setFieldValue("buyerTown", "");
          //         formRef.current.setFieldValue("buyerPostcode", "");
          //         formRef.current.setFieldValue("buyerLicenseNumber", "");
          //         setBuyerTeCertifiedVisbile(false);
          //         setVisible((prev) => ({
          //           ...prev,
          //           buyerSeacrhTEId: false,
          //           buyerSearchName: true,
          //           disableBuyerTEId: false,
          //           disableBuyerTEName: true,
          //         }));
          //         setDisable((prev) => ({
          //           ...prev,
          //           buyerCountry: false,
          //           buyerState: false,
          //           buyerPostCode: false,
          //         }));
          //       }
          //     },
          //   },
          //   props: {
          //     options: [
          //       { value: "Yes", label: "Yes" },
          //       { value: "N", label: "No" },
          //     ],
          //   },
          // },
          {
            label: "Is the Buyer certified?",
            name: "isBuyerTeCertified",
            type: "radio",
            gridSize: { xs: 12, md: 4 },
            required: true,
            value: initialValues.isBuyerTeCertified || "Yes",
            events: {
              onChange: (value: any, formValues: any, setFieldValue: any) => {
                if (value === "No") {
                  setFieldValue("buyerScCbLicensingCode", "");
                  setFieldValue("buyerSCNumber", "na");
                  setFieldValue("buyerCertifiedOrganizationName", "na");
                } else {
                  // Reset to default behavior for "Yes"
                  setFieldValue("buyerScCbLicensingCode", "");
                  setFieldValue("buyerSCNumber", "");
                  setFieldValue("buyerCertifiedOrganizationName", "");
                }
              },
            },
            props: {
              options: [
                { value: "Yes", label: "Yes" },
                { value: "No", label: "No" },
              ],
            },
          },
          {
            label: "Buyer TE-ID",
            name: "buyerTEId",
            type: "search",
            gridSize: { xs: 12, md: 4 },
            required: true,
            value: initialValues.buyerTEId,
            visible: () => true,
            props: {
              placeholder: "Search Buyer TE-ID",
              variant: "outlined",
              displayKey: "TE_ID",
              restrictInputFormat: visible.buyerTEIdLabel,
              onChange: withDependentTouchReset(
                handleBuyerTEIdValueChange,
                BUYER_DEPENDENT_FIELDS
              ),
              onSearch: (query: string, offset: number) => {
                const response = fetchTEDetallsFromAPI(query, offset, "query");
                return response;
              },
              fullWidth: true,
            },
          },
          {
            label: "Buyer TE-ID",
            name: "buyerTEId",
            type: "text",
            gridSize: { xs: 12, md: 4 },
            required: true,
            visible: () => false,
            props: {
              placeholder: "Enter Buyer TE-ID",
              fullWidth: true,
            },
          },
          // {
          //   label: "Buyer TE-ID",
          //   name: "buyerTEId",
          //   type: "text",
          //   gridSize: { xs: 12, md: 4 },
          //   required: false,
          //   visible: (values: any) => true,
          //   disabled: visible.disableBuyerTEId,
          //   props: {
          //     placeholder: "Enter Buyer TE ID",
          //     fullWidth: true,
          //   },
          // },
          {
            label: "Buyer Name",
            name: "buyerName",
            type: "search",
            gridSize: { xs: 12, md: 4 },
            required: true,
            value: initialValues.buyerName,
            visible: () => true,
            disabled: (values: any) =>
              !!(values?.buyerTEId && values?.sellerTEId && values?.buyerTEId === values?.sellerTEId),
            props: {
              placeholder: "Search Buyer Name",
              variant: "outlined",
              displayKey: "account_name",
              onChange: withDependentTouchReset(
                handleBuyerNameValueChange,
                BUYER_DEPENDENT_FIELDS
              ),
              onSearch: (query: string, offset: number) => {
                const response = fetchTEDetallsFromAPI(query, offset, "name");
                return response;
              },
              fullWidth: true,
            },
          },
          
          // ADD this new text field for Buyer Name when not certified:
          {
            label: "Buyer Name",
            name: "buyerName",
            type: "text",
            gridSize: { xs: 12, md: 4 },
            required: true,
            visible: () => false,
            disabled: (values: any) =>
              !!(values?.buyerTEId && values?.sellerTEId && values?.buyerTEId === values?.sellerTEId),
            props: {
              placeholder: "Enter Buyer Name",
              fullWidth: true,
            },
          },
          // {
          //   label: "Legal Name of Buyer",
          //   name: "buyerName",
          //   type: "search",
          //   gridSize: { xs: 12, md: 4 },
          //   required: getTCStandardGOTS ? false : true,
          //   value: initialValues.buyerName,
          //   visible: (values: any) => true,
          //   props: {
          //     placeholder: "Search Buyer Name",
          //     variant: "outlined",
          //     displayKey: "account_name",
          //     onChange: handleBuyerNameValueChange,
          //     onSearch: (query: string, offset: number) => {
          //       const response = fetchTEDetallsFromAPI(query, offset, "name");
          //       return response;
          //     },
          //     fullWidth: true,
          //   },
          // },
          {
            label: "Buyer Street Address",
            name: "buyerAddress1",
            type: "text",
            gridSize: { xs: 12, md: 4 },
            required: true,
            value: initialValues.buyerAddress1,
            disabled: (values: any) => {
              if (!disable.buyerAddress1) return false;
              return true;
            },
            props: {
              placeholder: "Enter Buyer Address",
              fullWidth: true,
            },
          },
          // {
          //   label: "Buyer Address 2",
          //   name: "buyerAddress2",
          //   type: "text",
          //   gridSize: { xs: 12, md: 4 },
          //   required: false,
          //   value: initialValues.buyerAddress2,
          //   disabled: (values: any) => values?.isBuyerTeCertified !== "N",
          //   props: {
          //     placeholder: "Enter Buyer Address 2",
          //     fullWidth: true,
          //   },
          // },
          // {
          //   label: "Buyer Address 3",
          //   name: "buyerAddress3",
          //   type: "text",
          //   gridSize: { xs: 12, md: 4 },
          //   required: false,
          //   value: initialValues.buyerAddress3,
          //   disabled: (values: any) => values?.isBuyerTeCertified !== "N",
          //   props: {
          //     placeholder: "Enter Buyer Address 3",
          //     fullWidth: true,
          //   },
          // },
          // Row 2: Column 1 - Town, Column 2 - Postcode, Column 3 - CB Client Number
          {
            label: "Buyer Town",
            name: "buyerTown",
            type: "text",
            gridSize: { xs: 12, md: 4 },
            required: true,
            value: initialValues.buyerTown,
            disabled: (values: any) => {
              if (!disable.buyerTown) return false;
              return true;
            },
            props: {
              placeholder: "Enter town",
              fullWidth: true,
            },
          },
          {
            label: "Buyer Postcode",
            name: "buyerPostcode",
            type: "text",
            gridSize: { xs: 12, md: 4 },
            required: true,
            value: initialValues.buyerPostcode,
            disabled: (values: any) => {
              if (!disable.buyerPostCode) return false;
              return true;
            },
            helperText: 'Please fill "na" if not available',
            props: {
              placeholder: "Enter postcode",
              fullWidth: true,
            },
          },
          {
            label: "Buyer CB Client Number",
            name: "buyerLicenseNumber",
            type: "text",
            gridSize: { xs: 12, md: 4 },
            required: false,
            value: initialValues.buyerLicenseNumber,
            // On a Return TC this is auto-filled by the SC No lookup below; same
            // rule as Seller Town once Seller TE-ID is selected: disabled once
            // the SC No has a value. Unaffected outside Return TC, where this
            // stays manually entered as before.
            disabled: isReturnTc && disable.buyerLicenseNumber,
            props: {
              placeholder: "Enter Buyer Client Number",
              variant: "outlined",
              fullWidth: true,
            },
          },
          // Row 3: Column 1 - Country/Area, Column 2 - State/Province, Column 3 - (empty)
          {
            label: "Buyer Country/Area",
            name: "buyerCountryOrArea",
            type: "select",
            gridSize: { xs: 12, md: 4 },
            required: true,
            value: initialValues.buyerCountryOrArea,
            disabled: (values: any) => {
              return disable.buyerCountry;
            },
            events: {
              onChange: (newValue: any, updatedValues: any) => {
                let countryCode = JSON.stringify(
                  `[${[newValue].map((item) => `'${item}'`)}]`
                ).replace(/^"|"$/g, "");
                getState(countryCode, true);
              },
            },
            props: {
              enableSearch: true,
              options: buyerCountryOptions,
              placeholder: "Select Buyer Country/Area",
              fullWidth: true,
            },
          },
          {
            label: "Buyer State/Province",
            name: "buyerStateOrProvince",
            type: "select",
            gridSize: { xs: 12, md: 4 },
            required: true,
            value: initialValues.buyerStateOrProvince,
            disabled: (values: any) => disable.buyerState,
            props: {
              options: getBuyerStates,
              enableSearch: true,
              placeholder: "Select Buyer State/Province",
              fullWidth: true,
            },
          },
          {
            label: "Buyer CB Licensing Code",
            name: "buyerScCbLicensingCode",
            type: "select",
            gridSize: { xs: 12, md: 4 },
            required: true,
            value: initialValues.buyerScCbLicensingCode,
            visible: (values: any) => values?.isBuyerTeCertified !== "No",
            events: {
              onChange: (_newValue: any, _updatedValues: any, setFieldValue: any) => {
                setFieldValue("buyerSCNumber", "");
                setFieldValue("buyerCertifiedOrganizationName", "");
                setFieldValue("buyerLicenseNumber", "");
              },
            },
            props: {
              enableSearch: true,
              options: buyerScCbLicensingCodes,
              placeholder: "Select Buyer CB Licensing Code",
              fullWidth: true,
            },
          },
          // Buyer SC search is scoped by both Buyer TE-ID and the selected CB.
          {
            label: "Buyer SC Number",
            name: "buyerSCNumber",
            type: "search",
            gridSize: { xs: 12, md: 4 },
            required: true,
            value: initialValues.buyerSCNumber,
            visible: (values: any) => values?.isBuyerTeCertified !== "No",
            disabled: (values: any) =>
              !values?.buyerTEId ||
              values?.buyerTEId === "" ||
              !values?.buyerScCbLicensingCode,
            props: {
              placeholder: "Search Buyer SC Number",
              variant: "outlined",
              displayKey: "label",
              // Works exactly like Seller SC Number/Seller TE-ID: pick from the
              // search results with the field's own "X" to clear, and (Return TC
              // only) Enter commits a typed "na" directly as a sentinel value.
              allowCustomValue: isReturnTc,
              commitInputPattern: isReturnTc ? /^na$/i : undefined,
              onChange: handleBuyerSCValueChange,
              onSearch: (query: string) => {
                const buyerTEId = formRef.current?.values?.buyerTEId;
                const buyerCbCode = formRef.current?.values?.buyerScCbLicensingCode;
                if (!buyerTEId || buyerTEId === "") {
                  notify("Please select Buyer TE ID first", "warning");
                  return Promise.resolve([]);
                }
                if (!buyerCbCode) {
                  notify("Please select Buyer CB Licensing Code first", "warning");
                  return Promise.resolve([]);
                }
                return fetchSCDetailsFromAPI(query, buyerTEId, 'Buyer');
              },
              fullWidth: true
            }
          },
          {
            label: "Buyer SC Number",
            name: "buyerSCNumber",
            type: "text",
            gridSize: { xs: 12, md: 4 },
            required: true,
            value: initialValues.buyerSCNumber,
            visible: (values: any) => values?.isBuyerTeCertified === "No",
            disabled: true,
            props: {
              placeholder: "na",
              fullWidth: true,
            }
          },
          // {
          //   label: "Buying On Behalf Of Flag",
          //   name: "buyingOnBehalfOfFlag",
          //   type: "radio",
          //   gridSize: { xs: 12, md: 4 },
          //   required: (value: any) => {
          //     if (getTCStandardGOTS) {
          //       return false;
          //     }
          //     return value?.buyingOnBehalfOfFlag == "Yes";
          //   },
          //   value: initialValues.buyingOnBehalfOfFlag,
          //   events: {
          //     onChange: (value: any) => {
          //       setBuyingOnBehalfOfFlag(value);
          //       if (value === "Yes") {
          //         formRef.current.setFieldValue(
          //           "buyerCertifiedOrganizationName",
          //           ""
          //         );
          //       } else {
          //         formRef.current.setFieldValue(
          //           "buyerCertifiedOrganizationName",
          //           getBuyerName
          //         );
          //       }
          //     },
          //   },
          //   props: {
          //     options: [
          //       { value: "Yes", label: "Yes" },
          //       { value: "N", label: "No" },
          //     ],
          //   },
          // },
          {
            label: "Buyer Certified Organization Name",
            name: "buyerCertifiedOrganizationName",
            type: "text",
            gridSize: { xs: 12, md: 4 },
            required: true,
            // readOnly: true,
            value: initialValues.buyerCertifiedOrganizationName,
            // On a Return TC this is auto-filled by the SC No lookup below; same
            // rule as Seller Town once Seller TE-ID is selected: disabled once
            // the SC No has a value.
            disabled: (values: any) =>
              isReturnTc
                ? disable.buyerCertifiedOrganizationName
                : values?.isBuyerTeCertified === "No",
            props: {
              placeholder: "Enter Certified Organization Name",
              variant: "outlined",
              fullWidth: true
            },
          },
        ],
      },
    ],
    handleSubmit: async (values: any) => {
      // The save POST runs on its own useApi instance, so the form stays
      // mounted (and every entered value stays put) whether or not it succeeds.

      // DAT-501 seller-SC rule 3 (86ajey1h1): block Save & Next when the attached
      // Seller SC does not carry every TC standard (covers SCs selected before a
      // later standard change on the Transaction Information tab).
      if (!isReturnTc) {
        const missingStandards = missingSellerScStandards(
          tcStandards,
          sellerScStandards
        );
        if (missingStandards.length > 0) {
          notify(
            `The Seller SC does not include the TC standard(s): ${missingStandards.join(
              ", "
            )}. Select a different Seller SC or change the TC standard.`,
            "error"
          );
          return;
        }
      }

      // SC existence checks based on tcStatus
      const sellerScNo = formRef.current?.values?.sellerSCNumber || "";
      const buyerScNo = formRef.current?.values?.buyerSCNumber || "";
      const sellerTeId = formRef.current?.values?.sellerTEId || "";
      const buyerTeId = formRef.current?.values?.buyerTEId || "";
      const buyerCbCode = formRef.current?.values?.buyerScCbLicensingCode || "";

      if (["A", "I"].includes(tcStatus)) {
        if (buyerScNo && !isNaValue(buyerScNo)) {
          setLoadingState(true);
          const verifyBuyer = await checkScExists(buyerScNo, buyerTeId, "Buyer", buyerCbCode || cbLicensingCode);
          setLoadingState(false);
          if (!verifyBuyer.exists) {
            notify("Buyer SC Number does not exist or has an invalid status.", "error");
            return;
          }
        }
        if (sellerScNo) {
          setLoadingState(true);
          const verifySeller = await checkScExists(sellerScNo, sellerTeId, "Seller", cbLicensingCode);
          setLoadingState(false);
          if (!verifySeller.exists) {
            notify("Seller SC Number does not exist or has an invalid status.", "error");
            return;
          }
        }
      }

      if (["R", "RA", "RI"].includes(tcStatus)) {
        if (buyerScNo && !isNaValue(buyerScNo)) {
          setLoadingState(true);
          const verifyBuyer = await checkScExists(buyerScNo, buyerTeId, "Buyer", buyerCbCode || cbLicensingCode);
          setLoadingState(false);
          if (!verifyBuyer.exists) {
            notify("Buyer SC Number does not exist or has an invalid status.", "error");
            return;
          }
        }
        if (sellerScNo && !isNaValue(sellerScNo)) {
          setLoadingState(true);
          const verifySeller = await checkScExists(sellerScNo, sellerTeId, "Seller", cbLicensingCode);
          setLoadingState(false);
          if (!verifySeller.exists) {
            notify("Seller SC Number does not exist or has an invalid status.", "error");
            return;
          }
        }
      }

      try {
        const buyerSellerDatas = getCurrentFormValues();
        if (isReturnTc && returnSourcePartiesRef.current) {
          // Validation sheet (return TC): buyer/seller must match the incoming
          // TC (roles reversed) on TE-ID, Name, Address, Town, Postcode,
          // Country and State only - SC Number, Certified Organization Name and
          // CB Client Number are intentionally not compared.
          const partyMismatches = findReturnTcPartyMismatches(
            buyerSellerDatas,
            returnSourcePartiesRef.current
          );
          if (partyMismatches.length > 0) {
            notify(returnTcPartyMismatchMessage(partyMismatches), "error");
            return;
          }
        }
        const payload = mapPayload(
          buyerSellerDatas,
          TE_ID,
          getBuyerStates,
          getSellerStates,
          sellerScStandards,
          isReturnTc
        );

        Utills.cl("Buyer Seller Form Submitted:", JSON.stringify(payload), 2);

        const result: any = await saveApi.executePost(
          TCAppConfig.POST_BUYER_SELLER_DETAILS_V4,
          payload
        );

        if (result?.status === 200) {
          notify("Saved Successfully", "success");

          // Use the tcId returned from API if available, otherwise fall back to route param
          const tcId = result.data?.tcId || TE_ID;

          // Let parent refresh completion map
          await onNextTab();

          // Navigate to next tab (Shipments) for create/edit flow
          if (!hasSubmitTc && tcId) {
            navigate(`/v4/tc/${formtype || "create-tc"}/shipment-details/${tcId}`);
          }
        } else {
          // Highlight the exact fields the backend rejected (it replies with a
          // [{field, message}] list) and keep the user on this tab.
          const fieldErrors = extractApiFieldErrors(result?.data);
          if (fieldErrors.length > 0 && formRef.current) {
            const unmapped = applyApiFieldErrorsToFormik(
              fieldErrors,
              formRef.current,
              BUYER_SELLER_API_FIELD_TO_FORM_FIELD
            );
            // Translate internal backend field paths to UI labels before
            // toasting (raw property names must never reach users - 86ajcn0z8).
            unmapped.forEach((fieldError) =>
              notify(
                fieldError.field
                  ? translateTcV4ValidationDetail({
                      field: fieldError.field,
                      message: fieldError.message,
                    })
                  : fieldError.message,
                "error"
              )
            );
            if (unmapped.length < fieldErrors.length) {
              notify(
                "Unable to save — please fix the highlighted fields",
                "error"
              );
              scrollToFirstFormError();
            }
          }
          // Otherwise useApi already toasted the specific backend error.
          return;
        }
      } catch (error) {
        console.error("Error saving Buyer & Seller Details:", error);
        notify("An error occurred while saving. Please try again.", "error");
        return;
      }
    },
  };

  // Ref to access the Formik instance in FormBuilder
  const formRef = useRef<any>(null);

  const handleSaveNonCertsTrader = async () => {
    formRef.current.setFieldValue(
      "nonCertifiedTrader",
      stepData.nonCertifiedTrader
    );
    updateField("nonCertifiedTrader", stepData.nonCertifiedTrader);
    if (stepData.nonCertifiedTrader.length === 0) {
      notify(
        'Non-Certified Trader Info removed. Please add at least one Non-Certified Trader Info',
        "warning"
      );
    }
    setDialogOpen(false);
  };

  const handleSubmit = async () => {
    try {
     if (hasSubmitTc) {
      navigate(`/v4/tc/${formtype}/shipment-details/${TE_ID}`);
    } else if (formRef.current) {
      // Validate up-front so an invalid form reports WHY instead of silently doing
      // nothing on "Save & Next" (the original Return-TC bug).
      const errors = await formRef.current.validateForm();
      const errorKeys = errors ? Object.keys(errors) : [];
      if (errorKeys.length > 0) {
        // Mark invalid fields touched so their inline errors render.
        formRef.current.setTouched(
          errorKeys.reduce((acc: any, key: string) => ({ ...acc, [key]: true }), {}),
          false
        );
        const firstError: any = errors[errorKeys[0]];
        const message =
          typeof firstError === "string"
            ? firstError
            : Array.isArray(firstError)
            ? "Please complete the Non-Certified Trader details"
            : "Please complete the highlighted required fields";
        notify(message, "error");
        scrollToFirstFormError();
        return;
      }
      formRef.current.submitForm(); // Programmatically submit the form
    }
    } catch (error) {
      console.error("Error in handleSubmit:", error);
      notify("Failed to save and navigate to next tab", "error");
    }
  }

  // const handleBack = () => {
  //     if (hasSubmitTc || hasRejectedTc) {
  //     // Navigate to previous tab in review mode
  //     navigate(`/v4/tc/${formtype}/base-details/${TE_ID}`);
  //   }
  // };

  return (
    <Box>
      {/* Render the FormBuilder component and pass formRef and onSubmit handler */}
      {loading || !isDataInitialized ? (
        <LoaderComponent />
      ) : (
        <>
          <FormBuilderV4
            formConfig={formConfig}
            onSubmit={formConfig.handleSubmit}
            formRef={formRef}
          />
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              mt: 2,
              gap: "0.5rem"
            }}
          >
             <NavLink
              to={`/v4/tc-dashboard`}
            >
              <StyledButton variant="contained"   sx={{ boxShadow:"none" }}>
                 Home
              </StyledButton>
            </NavLink>
             <NavLink
              to={`/v4/tc/${formtype}/base-details/${TE_ID}`}
            >
              <StyledButton variant="contained"   sx={{ boxShadow:"none" }}>
                 Back
              </StyledButton>
            </NavLink>
            <StyledButton
              variant="contained"
              sx={{ boxShadow: "none" }}
              onClick={handleSubmit}
              disabled={saveApi.loading}
            >
              {saveApi.loading ? "Saving..." : hasSubmitTc ? "Next" : "Save & Next"}
            </StyledButton>
          </Box>
        </>
      )}
      <ModalPopup
        open={dialogOpen}
        onSave={handleSaveNonCertsTrader}
        onClose={() => {
          setDialogOpen(false);
        }}
        disableSave = {isRestrictPopUpSave}
      >
        <NonCertifiedTraderInfo
          nonCertifiedInfoData={stepData.nonCertifiedTrader}
          setNonCertifiedInfoData={(data) =>
            handleDataUpdate("nonCertifiedTrader", data)
          }
        />
      </ModalPopup>
    </Box>
  );
};

export default SellerCertifiedProducts;
